import { Component, inject } from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { CardModule } from 'primeng/card'
import { ButtonModule } from 'primeng/button'
import { InputNumberModule } from 'primeng/inputnumber'
import { TagModule } from 'primeng/tag'
import { TooltipModule } from 'primeng/tooltip'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import {
  MapWithMarkersComponent,
  markerInputModel,
} from '../../../../core/components/map-with-markers/map-with-markers.component'
import { ToastService } from '../../../../shared/services/toast.service'
import {
  RoutabilityPoint,
  RoutabilityResult,
  RoutabilityService,
} from '../../services/routability.service'

type InputOrder = 'lat_lng' | 'lng_lat'

@Component({
  selector: 'app-routability-test',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    ButtonModule,
    InputNumberModule,
    TagModule,
    TooltipModule,
    ProgressSpinnerModule,
    MapWithMarkersComponent,
  ],
  templateUrl: './routability-test.component.html',
  styleUrl: './routability-test.component.scss',
})
export class RoutabilityTestComponent {
  private routabilityService = inject(RoutabilityService)
  private toast = inject(ToastService)

  public rawInput = ''
  public inputOrder: InputOrder = 'lat_lng'
  public minReachability = 20
  public loading = false
  public hasTested = false

  public results: RoutabilityResult[] = []
  public parseErrors: string[] = []
  public markers: markerInputModel[] = []
  public focusCoordinates: [number, number] | null = null

  public get routableCount(): number {
    return this.results.filter((r) => r.routable).length
  }

  public get unroutableCount(): number {
    return this.results.filter((r) => !r.routable).length
  }

  public test(): void {
    this.parseErrors = []
    const points = this.parseInput(this.rawInput)

    if (points.length === 0) {
      this.toast.showError('Aucune coordonnée valide à tester')
      return
    }

    this.loading = true
    this.hasTested = true
    this.routabilityService
      .testRoutability(points, this.minReachability)
      .subscribe({
        next: (res) => {
          this.results = res.results ?? []
          this.buildMarkers()
          this.loading = false
        },
        error: () => {
          this.toast.showError('Erreur lors du test de routabilité')
          this.loading = false
        },
      })
  }

  public fillExample(): void {
    this.rawInput = ['48.8566, 2.3522', '49.560427, -1.826790', '0, 0'].join(
      '\n'
    )
    this.inputOrder = 'lat_lng'
  }

  public clear(): void {
    this.rawInput = ''
    this.results = []
    this.parseErrors = []
    this.markers = []
    this.focusCoordinates = null
    this.hasTested = false
  }

  /**
   * Parse chaque ligne « a, b » (séparateur virgule, point-virgule ou espace).
   * L'ordre des deux nombres dépend de `inputOrder` :
   *  - lat_lng : latitude d'abord (convention Google Maps)
   *  - lng_lat : longitude d'abord (format affiché dans les erreurs VROOM/Valhalla)
   */
  private parseInput(raw: string): RoutabilityPoint[] {
    const points: RoutabilityPoint[] = []

    raw.split('\n').forEach((line, idx) => {
      const trimmed = line.trim()
      if (!trimmed) {
        return
      }

      const tokens = trimmed
        .split(/[,;\s]+/)
        .map((t) => t.trim())
        .filter((t) => t !== '')

      if (tokens.length < 2) {
        this.parseErrors.push(`Ligne ${idx + 1} ignorée : « ${trimmed} »`)
        return
      }

      const a = Number(tokens[0])
      const b = Number(tokens[1])

      if (!Number.isFinite(a) || !Number.isFinite(b)) {
        this.parseErrors.push(`Ligne ${idx + 1} ignorée : « ${trimmed} »`)
        return
      }

      const latitude = this.inputOrder === 'lat_lng' ? a : b
      const longitude = this.inputOrder === 'lat_lng' ? b : a

      points.push({ latitude, longitude })
    })

    return points
  }

  private buildMarkers(): void {
    const markers: markerInputModel[] = []

    this.results.forEach((result, index) => {
      markers.push({
        id: `input-${index}`,
        coordinates: [result.latitude, result.longitude],
        iconColor: result.routable ? '#16a34a' : '#dc2626',
        tooltipText: `${result.latitude}, ${result.longitude} — ${result.reason ?? ''}`,
        markerType: 'standard',
      })
    })

    this.markers = markers
    this.focusCoordinates =
      this.results.length === 1
        ? [this.results[0].latitude, this.results[0].longitude]
        : null
  }
}
